import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Spin } from 'antd';
import { useSession } from '../context/SessionContext';

export default function EditProfileView() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const { user, loading: sessionLoading } = useSession();

  useEffect(() => {
    if (sessionLoading || !user) return;

    fetch(`/api/users/${user}`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error("Failed to load user data");
        return res.json();
      })
      .then(userData => {
        form.setFieldsValue({ email: userData.email });
      })
      .catch(() => message.error('Failed to load user data'))
      .finally(() => setLoading(false));
  }, [sessionLoading, user, form]);

  const saveEdit = async () => {
    try {
      const values = await form.validateFields();
      const { confirmPassword, ...body } = values;
      const res = await fetch(`/api/users/${user}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const text = await res.text();
        if (res.status === 400 && text.includes('Current password')) {
          message.error('Current password is incorrect');
        } else {
          message.error('Update failed');
        }
        return;
      }
      message.success("Profile updated!");
      form.resetFields(['currentPassword', 'password', 'confirmPassword']);
    } catch (err) {
      console.error(err);
      message.error("Update failed");
    }
  };

  if (loading || sessionLoading) return <Spin style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div style={{ padding: 24, maxWidth: 400, margin: '0 auto' }}>
      <h2>Edit Profile</h2>
      <Form form={form} layout="vertical">
        <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="currentPassword" label="Current Password">
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="password"
          label="New Password"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value && !getFieldValue('currentPassword')) return Promise.resolve();
                if (value && getFieldValue('currentPassword')) return Promise.resolve();
                return Promise.reject(new Error('Enter your current password to set a new one'));
              }
            })
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={['password']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const newPass = getFieldValue('password');
                if (!newPass && !value) return Promise.resolve();
                if (value === newPass) return Promise.resolve();
                return Promise.reject(new Error('Passwords do not match'));
              }
            })
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={saveEdit}>
            Save Changes
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
